import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext.tsx';
import accountService from "@/services/accounts/AccountService.ts";

/**
 * Hook to fetch detailed information for a single account
 * Extends useAccounts pattern with single account query
 * 
 * @param accountId - The UUID of the account to fetch
 * @returns Query result with account data, loading state, and error
 */
export function useAccountDetail(accountId: string) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['account', accountId, user?.id],
    queryFn: async () => {
      return await accountService.fetchAccountById(accountId, user.id);
    },
    enabled: !!user && !!accountId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error) => {
      // Don't retry on 404 or invalid ID errors
      if (error.message === 'Account not found' || error.message === 'Invalid account ID format') {
        return false;
      }
      return failureCount < 3;
    },
  });
}
